import { useEffect, useState } from 'react';
import {
  BOT_ARCHETYPE_LIST,
  type Biome,
} from '@goodtime/shared';
import { BalancePill } from '../../BalancePill';
import { PillButton } from '../../ui/PillButton';
import { Icon } from '../../Icon';
import { CastAvatar } from '../../wheel/CastAvatar';
// Stage 3 install-excision (2026-06-06) — boss medallion now
// resolves via the asset manifest. Lazy getter so the lookup runs
// after {@code initManifestClient} has wired the dev fallback
// prefix (which only happens inside App's mount effect).
import { resolveEntryUrl } from '@goodtime/engine';
const bossMedallionUrl = (): string =>
  resolveEntryUrl({ path: 'map_node/boss.png' }) ?? '';

export interface MapHudProps {
  biome: Biome;
  /** Wallet coin balance (display number). */
  coinBalance: number;
  /** Wallet gem balance (display number). */
  gemBalance: number;
  /** True while the server wallet is still being fetched on a cold
   *  launch — the balance pill renders neutral placeholders instead of
   *  the local placeholder figures so the wallet never visibly jumps
   *  once the real balance syncs. Default false. */
  walletLoading?: boolean;
  /** Login streak (rendered as a 🔥 chip when > 0). */
  streak: number;
  /** Player level (rendered as a "Lvl X" chip). */
  level: number;
  /** Optional click handler for the daily-bonus button (sits next to
   * the balance pill). Opens the daily-bonus modal. Null hides the
   * button entirely — used after today's bonus has been claimed. */
  onDailyBonus?: (() => void) | null;
  /** Optional click handler for the main game crown badge (top-right).
   * Opens the cast lobby + creates a fresh Wheel room. */
  onMainGame?: () => void;
  /** Optional click handler — tapping the gem icon inside the balance
   * pill opens the shop. */
  onTapGems?: () => void;
  /** T2 (2026-05-29) — "All Episodes" button next to the title card.
   *  Pops back to the {@code BiomeListScreen} so the player can
   *  switch biomes without scrolling to the end of the strip. Null
   *  hides the button entirely (legacy / dev surfaces that aren't
   *  wired through the biome-list view). */
  onBackToBiomes?: (() => void) | null;
}

const HUD_HEIGHT = 96;
/** Diameter of the main game crown medallion. The crown is the
 * "main event" of the HUD — it's the player's hook into the
 * multiplayer Wheel match, sized accordingly so it dominates the
 * top-right corner. */
const CROWN_SIZE = 200;
/** Diameter of each cast avatar bubble around the medallion. */
const AVATAR_SIZE = 60;
/** How far the avatar centres sit from the medallion centre. */
const AVATAR_RING_RADIUS = CROWN_SIZE / 2 + 22;
/** Viewport-width breakpoint (px) below which the crown medallion
 *  centres under the title card. At/above it, the crown stays
 *  right-aligned (the classic desktop layout — the slot machines fan
 *  out below and the crown reads as the top-right "main event").
 *  Producer 2026-05-29: only mobile needed centring. Matches the
 *  reel-race seat-rail breakpoint for consistency. */
const CROWN_CENTER_BREAKPOINT_PX = 640;

/**
 * Static top-overlay HUD for the V2 map — sits above the scrollable
 * map content (does not scroll). Mirrors the user's reference mockup.
 *
 * <ul>
 *   <li>centre-left: episode title card with biome chapter + name + blurb
 *   <li>right cluster: small daily-bonus gift button → coin+gem balance
 *       pill (gem area opens the shop) → streak chip (when streak > 0)
 *       → level chip → main game crown medallion with cast avatar ring
 * </ul>
 *
 * <p>Travel between biomes is handled by the EPISODES button (→ the
 * all-episodes list), not by in-strip portals or a HUD chevron.
 */
export function MapHud({
  biome,
  coinBalance,
  gemBalance,
  walletLoading = false,
  streak,
  level,
  onDailyBonus,
  onMainGame,
  onTapGems,
  onBackToBiomes = null,
}: MapHudProps) {
  // Crown is centred only on phones; desktop keeps it right-aligned.
  // Tracked live so a resize across the breakpoint re-flows correctly.
  const [crownCentered, setCrownCentered] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.innerWidth < CROWN_CENTER_BREAKPOINT_PX,
  );
  useEffect(() => {
    const onResize = (): void =>
      setCrownCentered(window.innerWidth < CROWN_CENTER_BREAKPOINT_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        // iOS pre-flight fix (2026-06-07) — push the HUD root down by
        // the device safe-area-inset-top so the title card + episodes
        // pill + wallet chips don't collide with the Dynamic Island.
        // env() is 0 on the web (no layout change for desktop).
        top: 'env(safe-area-inset-top, 0px)',
        left: 0,
        right: 0,
        // Tall enough to host the crown medallion that now hangs below
        // the title card. Children are absolutely positioned so the
        // crown's 320px footprint can't push the wallet chips off the
        // right edge (the old flex-row layout did exactly that on
        // phones — producer flag 2026-05-29).
        height: HUD_HEIGHT + 24 + CROWN_SIZE,
        zIndex: 40,
        pointerEvents: 'none',
        fontFamily: '"Fredoka", system-ui',
      }}
      aria-label="map hud"
    >
      {/* T2 — "All Episodes" back-button sits above the title card.
          Always-available escape hatch to the BiomeListScreen so the
          player can swap biomes without scrolling to the end of the
          current strip. Hidden when no handler is wired. */}
      {onBackToBiomes ? (
        <PillButton
          onClick={onBackToBiomes}
          ariaLabel="Back to all episodes"
          size="sm"
          uppercase
          icon={<Icon name="back" size={13} alt="" />}
          style={{
            position: 'absolute',
            top: 4,
            left: 18,
            zIndex: 42,
            pointerEvents: 'auto',
            // Match the coin HUD's height (44) so the two top pills line
            // up — producer 2026-06-07: the size gap looked odd.
            height: 44,
            padding: '0 18px',
            fontSize: 14,
          }}
        >
          Episodes
        </PillButton>
      ) : null}

      {/* Title card — absolutely positioned top-left. Episode/chapter
          eyebrow dropped (producer 2026-05-29); the biome name carries
          the card on its own at a larger size. The "All Episodes"
          back-button (when wired) sits in the same corner above it. */}
      <div
        style={{
          position: 'absolute',
          // Mobile: the full-width band is a header BELOW the back/wallet
          // row, so it must clear the wallet pill (top:4 + 44h = ~48).
          // Desktop keeps the left-card under the back button.
          top: crownCentered ? 56 : onBackToBiomes ? 50 : 14,
          // Mobile (producer 2026-06-07): the name reads as a full-width
          // header band beneath the back/wallet row, with the multiplayer
          // crown sitting directly under it. Desktop keeps the compact
          // left-aligned card so the slot row can fan out beside it.
          ...(crownCentered
            ? { left: 14, right: 14 }
            : {
                left: 18,
                minWidth: 'clamp(150px, 16vw, 320px)',
                maxWidth: 'clamp(170px, 44vw, 380px)',
              }),
          pointerEvents: 'none',
          padding: 'clamp(12px, 1.4vw, 20px) clamp(18px, 2.4vw, 36px)',
          borderRadius: 18,
          background: `linear-gradient(180deg, ${biome.accent}, ${biome.sky[2]})`,
          color: '#fff',
          textAlign: 'center',
          textShadow: '0 1px 0 rgba(0,0,0,0.55)',
          boxShadow:
            '0 6px 14px rgba(0,0,0,0.45), inset 0 -2px 3px rgba(0,0,0,0.25), inset 0 2px 3px rgba(255,255,255,0.45)',
          border: '2px solid rgba(255,255,255,0.85)',
        }}
      >
        <div
          style={{
            fontSize: 'clamp(24px, 2.6vw, 40px)',
            fontWeight: 800,
            lineHeight: 1.02,
          }}
        >
          {biome.name}
        </div>
        <div
          style={{
            fontSize: 'clamp(12px, 1vw, 16px)',
            fontStyle: 'italic',
            opacity: 0.95,
            marginTop: 4,
          }}
        >
          “{biome.blurb}”
        </div>
      </div>

      {/* Main game crown medallion — boss.png sunburst-red base with a
          ring of cast-archetype avatars (the "main game with the cast"
          mockup look). Responsive anchor: on phones it centres below
          the title card (hero CTA, no room on the sides); on desktop it
          stays right-aligned below the wallet (the classic layout — the
          slot-machine row fans out below). All three HUD clusters (card
          top-left, wallet top-right, crown) are independently absolute,
          so the crown's 320px width never pushes the others. */}
      <div
        style={{
          position: 'absolute',
          // Mobile: drop the crown below the full-width name band so it
          // reads as that header's CTA. Desktop keeps it at the classic
          // height tucked under the wallet cluster.
          top: crownCentered ? HUD_HEIGHT + 60 : HUD_HEIGHT + 12,
          ...(crownCentered
            ? { left: '50%', transform: 'translateX(-50%)' }
            : { right: 4, transform: 'none' }),
          width: CROWN_SIZE + AVATAR_SIZE * 2,
          height: CROWN_SIZE + AVATAR_SIZE * 2,
          pointerEvents: 'none',
        }}
      >
          {/* Avatars distributed around the perimeter. */}
          {BOT_ARCHETYPE_LIST.map((meta, i) => {
            const total = BOT_ARCHETYPE_LIST.length;
            const angle = (-90 + (i / total) * 360) * (Math.PI / 180);
            const cx = AVATAR_SIZE + CROWN_SIZE / 2 + Math.cos(angle) * AVATAR_RING_RADIUS;
            const cy = AVATAR_SIZE + CROWN_SIZE / 2 + Math.sin(angle) * AVATAR_RING_RADIUS;
            return (
              <div
                key={meta.id}
                style={{
                  position: 'absolute',
                  left: cx,
                  top: cy,
                  transform: 'translate(-50%, -50%)',
                  width: AVATAR_SIZE,
                  height: AVATAR_SIZE,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: meta.accent,
                  border: '3px solid #fff',
                  boxShadow:
                    '0 3px 6px rgba(0,0,0,0.45), inset 0 -2px 3px rgba(0,0,0,0.18)',
                  zIndex: 1,
                }}
              >
                <CastAvatar archetype={meta.id} size={AVATAR_SIZE} />
              </div>
            );
          })}

          {/* Crown medallion button (clickable centre) */}
          <button
            type="button"
            onClick={onMainGame}
            style={{
              position: 'absolute',
              top: AVATAR_SIZE,
              left: AVATAR_SIZE,
              width: CROWN_SIZE,
              height: CROWN_SIZE,
              borderRadius: '50%',
              border: 'none',
              background: 'transparent',
              padding: 0,
              cursor: onMainGame ? 'pointer' : 'default',
              pointerEvents: 'auto',
              zIndex: 2,
              filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.5))',
            }}
            aria-label="Main game"
          >
            <img
              src={bossMedallionUrl()}
              alt=""
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
              }}
            />
          </button>
        </div>

      {/* Wallet cluster — absolutely positioned top-right so the crown's
          width below can't shove it off-screen. Stacked as a column
          (compact pill on top, daily-bonus gift beneath it) so the
          whole cluster stays the pill's width (~167px) and clears the
          episode card on phones — a horizontal row would push the gift
          left into the card. */}
      <div
        style={{
          position: 'absolute',
          top: 4,
          right: 18,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 10,
        }}
      >
        {/* Wallet pill — compact on the map so it fits beside the
            episode card on narrow phones. Coin + gem only; streak /
            level are dropped (they're wallet-progression noise on the
            lobby ambiance frame). Full pill still renders in slot /
            wheel scenes. */}
        <BalancePill
          coinBalance={coinBalance}
          gemBalance={gemBalance}
          streak={streak}
          level={level}
          onTapGems={onTapGems}
          loading={walletLoading}
          compact
        />

        {/* Daily-bonus gift button — sits beneath the pill, right-
            aligned. Hidden when onDailyBonus is null (today's bonus
            already claimed). */}
        {onDailyBonus ? (
          <button
            type="button"
            onClick={onDailyBonus}
            style={{
              pointerEvents: 'auto',
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: '2px solid #FFE9A8',
              background:
                'radial-gradient(circle at 35% 25%, #FFE57F, #FFC40C 60%, #B07F00)',
              boxShadow:
                '0 3px 6px rgba(0,0,0,0.35), inset 0 -2px 3px rgba(120,90,0,0.45), inset 0 1px 2px rgba(255,255,255,0.5)',
              cursor: 'pointer',
              fontSize: 18,
              color: '#5A3A1A',
              padding: 0,
            }}
            aria-label="Daily bonus"
          >
            🎁
          </button>
        ) : null}
      </div>
    </div>
  );
}
